import { pgTable, serial, integer, text, timestamp, boolean, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// NOTE: the legacy payroll_integrations table (per-client provider/apiKey
// rows) was removed — payroll provider connections live in
// shared/schemas/integrations.ts (client_payroll_connections).

// Pay groups (one or many per client). A client can run several pay calendars
// (e.g. salaried bi-weekly + hourly weekly); each row is one group. Employees
// link via employees.payGroupId; unassigned employees use the client's default.
// (Physical table name kept as "payroll_settings" — rename deferred.)
export const payrollSettings = pgTable("payroll_settings", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  name: text("name").notNull().default("Default"), // pay-group label, e.g. "Salaried — Bi-weekly"
  isDefault: boolean("is_default").notNull().default(true), // fallback group for unassigned employees
  payFrequency: text("pay_frequency").notNull().default("biweekly"), // weekly | biweekly | semimonthly | monthly
  payDay: text("pay_day").notNull().default("friday"), // monday..friday (for weekly/biweekly) or day spec descriptor
  payrollCutoff: text("payroll_cutoff").default("2 business days before pay day"), // Legacy text field (kept for compatibility)
  periodStartDate: timestamp("period_start_date"), // Anchor date for pay period calculations
  payrollCutoffDays: integer("payroll_cutoff_days").default(2), // Business days before pay date
  payDateOffset: integer("pay_date_offset").default(0), // Days after period end when pay is issued
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  clientIdx: index("payroll_settings_client_id_idx").on(t.clientId),
}));

export const insertPayrollSettingsSchema = createInsertSchema(payrollSettings, {
  name: z.string().min(1).max(100).default("Default"),
  isDefault: z.boolean().default(true),
  payFrequency: z.enum(["weekly", "biweekly", "semimonthly", "monthly"]).default("biweekly"),
  payDay: z.enum(["monday", "tuesday", "wednesday", "thursday", "friday"]).default("friday"),
  payrollCutoff: z.string().min(2).max(100).default("2 business days before pay day"),
  periodStartDate: z.string().datetime().optional().nullable(),
  payrollCutoffDays: z.number().int().min(0).max(10).default(2),
  payDateOffset: z.number().int().min(0).max(14).default(0),
}).omit({ id: true, clientId: true, createdAt: true, updatedAt: true });

export type PayrollSettings = typeof payrollSettings.$inferSelect;
export type InsertPayrollSettings = z.infer<typeof insertPayrollSettingsSchema>;
